import { useContext } from 'react';
import { type DayButtonProps, type DayProps } from 'react-day-picker';
import classNames from 'classnames';

import { AvailabilityDayButtonContext } from '../../contexts';
import { getDateKey } from './availabilityCalendar';
import styles from './AvailabilitiesContainer.module.scss';

type AvailabilityDayContentProps = {
    readonly appointmentCountLabel: string;
    readonly className?: string;
    readonly date: Date;
    readonly isAvailable: boolean;
    readonly isOutsideDay: boolean;
};

const AvailabilityDayContent = ({
    appointmentCountLabel,
    className,
    date,
    isAvailable,
    isOutsideDay,
}: AvailabilityDayContentProps): JSX.Element => (
    <div
        className={classNames(
            className,
            styles.dayButton,
            'd-flex flex-column align-items-center justify-content-sm-between justify-content-center w-100 h-100 opacity-100 p-sm-2x p-0',
            isOutsideDay && styles.dayButtonOutside,
            isAvailable ? styles.dayButtonAvailable : styles.dayButtonOutside,
        )}
    >
        <span className={classNames(styles.dayNumber, 'align-self-center align-self-sm-end')}>
            {date.getDate()}
        </span>
        <span
            className={classNames(
                styles.dayCount,
                'd-sm-flex d-none align-items-end justify-content-center flex-1 w-100 text-center',
            )}
        >
            {isAvailable ? appointmentCountLabel : '—'}
        </span>
    </div>
);

const AvailabilityDay = ({ className, day }: DayButtonProps): JSX.Element => {
    const { availabilityCountByDate, formatAppointmentCount } = useContext(
        AvailabilityDayButtonContext,
    );
    const dateKey = getDateKey(day.date);
    const terminCount = availabilityCountByDate.get(dateKey) ?? 0;
    const isAvailable = terminCount > 0;
    const appointmentCountLabel = formatAppointmentCount(terminCount);
    const isOutsideDay = day.outside;

    return (
        <div className="h-100" data-testid={`availability-day-${dateKey}`}>
            <AvailabilityDayContent
                appointmentCountLabel={appointmentCountLabel}
                className={className}
                date={day.date}
                isAvailable={isAvailable}
                isOutsideDay={isOutsideDay}
            />
            {isAvailable ? (
                <span className="screenReaderOnly" data-testid={`availability-count-${dateKey}`}>
                    {appointmentCountLabel}
                </span>
            ) : null}
        </div>
    );
};

const HiddenDay = ({ children, className, day, modifiers, ...props }: DayProps): JSX.Element => {
    const { availabilityCountByDate, formatAppointmentCount } = useContext(
        AvailabilityDayButtonContext,
    );

    if (!modifiers.hidden) {
        return (
            <td className={className} {...props}>
                {children}
            </td>
        );
    }

    const dateKey = getDateKey(day.date);
    const terminCount = availabilityCountByDate.get(dateKey) ?? 0;

    return (
        <td className={classNames(className, 'visible')} {...props}>
            <div className="h-100" data-testid={`availability-day-${dateKey}`}>
                <AvailabilityDayContent
                    appointmentCountLabel={formatAppointmentCount(terminCount)}
                    date={day.date}
                    isAvailable={terminCount > 0}
                    isOutsideDay
                />
            </div>
        </td>
    );
};

export { AvailabilityDay, AvailabilityDayContent, HiddenDay };
